import { Link } from '@inertiajs/react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import classNames from 'classnames';
import { ChevronDown, Database, Download, Link2, LogOut, Settings, TrendingUp, Users } from 'react-feather';
import { useTranslation } from 'react-i18next';

import { useMutation } from '../../Shared/api';
import { Charity, User } from '../../Shared/types';
import { Icon } from '../../Shared/UI/Icon';
import { NameBadge } from '../../Shared/UI/NameBadge';
import { useRouter } from '../router';

export function UserNav({
  user,
  charity,
}: {
  user: User & { charities: Charity[]; admin: boolean; };
  charity?: Charity | null;
}) {
  return (
    <div className={classNames('text-right text-sm space-x-4 text-blue-200/80 flex items-center justify-end min-w-0')}>
      {user.charities && user.charities.length > 0 && <CharityDropdown user={user} charity={charity} />}
      <UserDropdown user={user} />
    </div>
  );
}

function CharityDropdown({
  user,
  charity,
}: {
  user: User & { charities: Charity[]; admin: boolean; };
  charity?: Charity | null;
}) {
  const { t } = useTranslation();
  const { routes } = useRouter();

  const charities = user.charities.filter(({ id }) => id !== charity?.id);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button className="relative h-10 pl-1 pr-6 overflow-hidden rounded outline-none focus:ring focus:ring-blue-300/30 whitespace-nowrap text-ellipsis">
          {charity?.title || t('backend:charities')}
          <Icon className="absolute ml-2 right-1 top-[15px]">
            <ChevronDown />
          </Icon>
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" className="relative z-[51] p-2 leading-tight bg-white border rounded-lg shadow-sm border-black/10">
          {charity && (
            <>
              <DropdownMenu.Item asChild>
                <Link href={routes.charity_settings_page()} className="block p-1 rounded outline-none text-slate-700 focus:text-slate-900 focus:bg-slate-100">
                  <Icon className="mr-2">
                    <Settings />
                  </Icon>
                  {t('backend:organisation')}
                </Link>
              </DropdownMenu.Item>
              <DropdownMenu.Item asChild>
                <Link href={routes.mollie_page()} className="block p-1 rounded outline-none text-slate-700 focus:text-slate-900 focus:bg-slate-100">
                  <Icon className="mr-2">
                    <Link2 />
                  </Icon>
                  {t('backend:mollie')}
                </Link>
              </DropdownMenu.Item>
              <DropdownMenu.Item asChild>
                <Link href={routes.reports_page()} className="block p-1 rounded outline-none text-slate-700 focus:text-slate-900 focus:bg-slate-100">
                  <Icon className="mr-2">
                    <TrendingUp />
                  </Icon>
                  {t('backend:reports')}
                </Link>
              </DropdownMenu.Item>
              <DropdownMenu.Item asChild>
                <Link href={routes.users_page()} className="block p-1 rounded outline-none text-slate-700 focus:text-slate-900 focus:bg-slate-100">
                  <Icon className="mr-2">
                    <Users />
                  </Icon>
                  {t('backend:users')}
                </Link>
              </DropdownMenu.Item>
              <DropdownMenu.Item asChild>
                <Link href={routes.exports_page()} className="block p-1 rounded outline-none text-slate-700 focus:text-slate-900 focus:bg-slate-100">
                  <Icon className="mr-2">
                    <Download />
                  </Icon>
                  {t('backend:exports')}
                </Link>
              </DropdownMenu.Item>
              {charities.length > 0 && <DropdownMenu.Separator className="my-2 border-t" />}
            </>
          )}
          {charities.map((charity) => (
            <DropdownMenu.Item asChild key={charity.id}>
              <Link href={routes.home(charity.id)} className="block p-1 rounded outline-none text-slate-700 focus:text-slate-900 focus:bg-slate-100">
                <Icon className="mr-2">
                  <Database />
                </Icon>
                {charity.title}
              </Link>
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

function UserDropdown({
  user,
}: {
  user: User;
}) {
  const { t } = useTranslation();
  const { routes } = useRouter();
  const [logout] = useMutation(routes.logout());

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button className="h-10 px-1 rounded outline-none focus:ring focus:ring-blue-300/30 whitespace-nowrap">
          <NameBadge name={user.first_name} variant="custom" className="text-white bg-emerald-500" />
          <Icon className="ml-1">
            <ChevronDown />
          </Icon>
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content align="end" className="relative z-[51] p-2 leading-tight bg-white border rounded-lg shadow-sm border-black/10">
          <DropdownMenu.Label className="p-1 text-slate-400">
            {user.first_name}{' '}{user.last_name}
          </DropdownMenu.Label>
          <DropdownMenu.Item asChild>
            <button onClick={() => logout()} className="block w-full p-1 text-left rounded outline-none text-slate-700 focus:text-slate-900 focus:bg-slate-100">
              <Icon className="mr-2">
                <LogOut />
              </Icon>
              {t('backend:log_out')}
            </button>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
